import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { X, Briefcase, AlertCircle, Loader2, User, Check, ChevronDown, ChevronUp, Sparkles, MapPin, Clock, Euro } from 'lucide-react';
import { createOffer, OFFER_TYPES, URGENCY_BADGES, COMPENSATION_TYPES, DURATION_TYPES } from '../../services/workOffersService';
import { getStoredUser as getUser } from '../../context/AuthContext';
import { OccupationAutocomplete } from '../occupations';
import { getPeopleOccupations, getPeopleSkills, getSkillsForOccupation, fetchEscoSkillsForOccupation } from '../../services/skillsService';
import { getOccupationById } from '../../services/occupationsService';

const CreateWorkOffer = ({ onClose, onCreated }) => {
    const { t } = useTranslation();
    const user = getUser();

    const [formData, setFormData] = useState({
        offerType: 'job_fulltime',
        title: '',
        description: '',
        location: '',
        urgencyBadge: 'asap',
        compensation: 'paid',
        duration: 'undefined',
        occupationIds: [],
        skillIds: []
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    // Use profile data toggle
    const [useProfile, setUseProfile] = useState(false);
    const [profileOccupations, setProfileOccupations] = useState([]);
    const [profileSkills, setProfileSkills] = useState([]);
    const [loadingProfile, setLoadingProfile] = useState(false);

    // Manual occupation selection - like OccupationsSkillsPanel
    const [selectedOccupations, setSelectedOccupations] = useState([]);
    const [expandedOccupation, setExpandedOccupation] = useState(null);
    const [occupationSkills, setOccupationSkills] = useState({});
    const [selectedSkillIds, setSelectedSkillIds] = useState([]);
    const [loadingSkills, setLoadingSkills] = useState({});
    const [isAddingOccupation, setIsAddingOccupation] = useState(true);

    // Load profile occupations and skills when use profile mode is activated
    useEffect(() => {
        if (useProfile && user?.uuid) {
            loadProfileData();
        }
    }, [useProfile, user?.uuid]);

    const loadProfileData = async () => {
        if (!user?.uuid) return;
        setLoadingProfile(true);
        try {
            const occupations = await getPeopleOccupations(user.uuid, 'fr');
            setProfileOccupations(occupations);

            const { skills, skillIds } = await getPeopleSkills(user.uuid, null, 'fr');
            setProfileSkills(skills);

            setFormData(prev => ({
                ...prev,
                occupationIds: occupations.map(o => o.id),
                skillIds: skillIds || skills.map(s => s.id)
            }));
        } catch (error) {
            console.error('Error loading profile data:', error);
        }
        setLoadingProfile(false);
    };

    // Load skills for an occupation
    const loadSkillsForOccupation = useCallback(async (occupationId) => {
        if (occupationSkills[occupationId]) return;

        setLoadingSkills(prev => ({ ...prev, [occupationId]: true }));

        try {
            let skills = await getSkillsForOccupation(occupationId, 'fr');

            if (skills.length === 0) {
                await fetchEscoSkillsForOccupation(occupationId);
                skills = await getSkillsForOccupation(occupationId, 'fr');
            }

            setOccupationSkills(prev => ({ ...prev, [occupationId]: skills }));
        } catch (error) {
            console.error('Error loading skills:', error);
        } finally {
            setLoadingSkills(prev => ({ ...prev, [occupationId]: false }));
        }
    }, [occupationSkills]);

    // Toggle occupation expansion
    const handleToggleExpand = (occupationId) => {
        if (expandedOccupation === occupationId) {
            setExpandedOccupation(null);
        } else {
            setExpandedOccupation(occupationId);
            loadSkillsForOccupation(occupationId);
        }
    };

    // Add occupation
    const handleAddOccupation = async (occupationId, occupation) => {
        if (!occupationId) return;

        const fullOccupation = await getOccupationById(occupationId);
        if (fullOccupation) {
            setSelectedOccupations(prev => [...prev, fullOccupation]);
            setFormData(prev => ({
                ...prev,
                occupationIds: [...prev.occupationIds, occupationId]
            }));
            setIsAddingOccupation(false);
            setExpandedOccupation(occupationId);
            loadSkillsForOccupation(occupationId);
        }
    };

    // Remove occupation
    const handleRemoveOccupation = (occupationId) => {
        setSelectedOccupations(prev => prev.filter(o => o.id !== occupationId));
        setFormData(prev => ({
            ...prev,
            occupationIds: prev.occupationIds.filter(id => id !== occupationId)
        }));
        setOccupationSkills(prev => {
            const { [occupationId]: _, ...rest } = prev;
            return rest;
        });
        if (occupationSkills[occupationId]) {
            const skillIdsToRemove = occupationSkills[occupationId].map(s => s.id);
            setSelectedSkillIds(prev => prev.filter(id => !skillIdsToRemove.includes(id)));
            setFormData(prev => ({
                ...prev,
                skillIds: prev.skillIds.filter(id => !skillIdsToRemove.includes(id))
            }));
        }
        if (expandedOccupation === occupationId) {
            setExpandedOccupation(null);
        }
    };

    // Toggle skill selection
    const handleSkillToggle = (skillId) => {
        setSelectedSkillIds(prev => {
            const newIds = prev.includes(skillId)
                ? prev.filter(id => id !== skillId)
                : [...prev, skillId];

            setFormData(prevForm => ({
                ...prevForm,
                skillIds: newIds
            }));

            return newIds;
        });
    };

    // Get display name
    const getDisplayName = (item) => {
        return item?.name_fr || item?.name_en || '';
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = t.offers?.errors?.titleRequired || 'Le titre est requis';
        }
        if (!formData.description.trim()) {
            newErrors.description = t.offers?.errors?.descriptionRequired || 'La description est requise';
        }
        if (formData.description.length < 20) {
            newErrors.description = t.offers?.errors?.descriptionTooShort || 'La description doit faire au moins 20 caractères';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerError('');

        if (!validate()) return;

        setLoading(true);
        const result = await createOffer({
            userId: user?.id,
            ...formData
        });

        setLoading(false);

        if (result.success) {
            onCreated();
        } else {
            setServerError(result.message || t.offers?.errors?.createFailed || 'Erreur lors de la création');
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                    <div className="flex items-center gap-3">
                        <Briefcase className="w-6 h-6" />
                        <h2 className="text-xl font-semibold">
                            {t.offers?.createTitle || 'Nouvelle offre de travail'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    {serverError && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            <span>{serverError}</span>
                        </div>
                    )}

                    {/* Offer Type */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.offers?.form?.type || "Type d'offre"} *
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {Object.entries(OFFER_TYPES).map(([key, { label, color }]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => handleChange('offerType', key)}
                                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                                        formData.offerType === key
                                            ? 'border-current shadow-md'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    style={{
                                        borderColor: formData.offerType === key ? color : undefined,
                                        backgroundColor: formData.offerType === key ? `${color}10` : undefined
                                    }}
                                >
                                    <span
                                        className="inline-block w-3 h-3 rounded-full mr-2"
                                        style={{ backgroundColor: color }}
                                    ></span>
                                    <span className="text-sm font-medium">{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Title */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.offers?.form?.title || 'Titre'} *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder={t.offers?.form?.titlePlaceholder || 'Ex: Développeur Web React disponible'}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${
                                errors.title ? 'border-red-500' : 'border-gray-300'
                            }`}
                            maxLength={255}
                        />
                        {errors.title && (
                            <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.offers?.form?.description || 'Description'} *
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder={t.offers?.form?.descriptionPlaceholder || 'Décrivez en détail vos compétences et ce que vous proposez...'}
                            rows={4}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 resize-none ${
                                errors.description ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {errors.description && (
                            <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">
                            {formData.description.length} / 20 {t.offers?.form?.minChars || 'caractères minimum'}
                        </p>
                    </div>

                    {/* Location */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <MapPin className="inline w-4 h-4 mr-1" />
                            {t.offers?.form?.location || 'Lieu'}
                        </label>
                        <input
                            type="text"
                            value={formData.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                            placeholder={t.offers?.form?.locationPlaceholder || 'Ex: Bruxelles, Télétravail...'}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                    </div>

                    {/* Urgency / Availability */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Clock className="inline w-4 h-4 mr-1" />
                            {t.offers?.form?.urgency || 'Disponibilité'}
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(URGENCY_BADGES).map(([key, { label, color }]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => handleChange('urgencyBadge', key)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.urgencyBadge === key
                                            ? 'text-white shadow-md'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    style={{
                                        backgroundColor: formData.urgencyBadge === key ? color : undefined
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Compensation */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Euro className="inline w-4 h-4 mr-1" />
                            {t.offers?.form?.compensation || 'Contrepartie souhaitée'}
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(COMPENSATION_TYPES).map(([key, { label, color }]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => handleChange('compensation', key)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.compensation === key
                                            ? 'text-white shadow-md'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    style={{
                                        backgroundColor: formData.compensation === key ? color : undefined
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Duration */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.offers?.form?.duration || 'Durée de disponibilité'}
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(DURATION_TYPES).map(([key, { label, color }]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => handleChange('duration', key)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.duration === key
                                            ? 'text-white shadow-md'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    style={{
                                        backgroundColor: formData.duration === key ? color : undefined
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Use Profile Toggle */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <User className="w-5 h-5 text-green-600" />
                                <div>
                                    <span className="font-medium text-gray-800">
                                        {t.offers?.form?.useProfile || 'Utiliser mon profil'}
                                    </span>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {t.offers?.form?.useProfileHelp || 'Utiliser les métiers et compétences de mon profil'}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setUseProfile(!useProfile);
                                    if (useProfile) {
                                        setFormData(prev => ({ ...prev, occupationIds: [], skillIds: [] }));
                                        setSelectedOccupations([]);
                                        setOccupationSkills({});
                                        setSelectedSkillIds([]);
                                    }
                                }}
                                className={`relative w-14 h-7 rounded-full transition-colors ${
                                    useProfile ? 'bg-green-600' : 'bg-gray-300'
                                }`}
                            >
                                <span
                                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                                        useProfile ? 'translate-x-7' : 'translate-x-0'
                                    }`}
                                />
                            </button>
                        </div>

                        {/* Use Profile ON: Profile data */}
                        {useProfile && (
                            <div className="mt-4 pt-4 border-t border-green-200">
                                {loadingProfile ? (
                                    <div className="flex items-center justify-center py-4">
                                        <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
                                        <span className="ml-2 text-gray-600">
                                            {t.offers?.form?.loadingProfile || 'Chargement du profil...'}
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        {profileOccupations.length > 0 ? (
                                            <div className="mb-3">
                                                <span className="text-sm font-medium text-gray-700">
                                                    {t.offers?.form?.myOccupations || 'Mes métiers'}:
                                                </span>
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {profileOccupations.map(occ => (
                                                        <span
                                                            key={occ.id}
                                                            className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                                                        >
                                                            {occ.name_fr || occ.name_en}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-amber-600 mb-3">
                                                {t.offers?.form?.noOccupations || 'Aucun métier dans votre profil.'}
                                            </p>
                                        )}

                                        {profileSkills.length > 0 ? (
                                            <div>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {t.offers?.form?.mySkills || 'Mes compétences'}:
                                                </span>
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {profileSkills.map(skill => (
                                                        <span
                                                            key={skill.id}
                                                            className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs"
                                                        >
                                                            {skill.name_fr || skill.name_en}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-amber-600">
                                                {t.offers?.form?.noSkills || 'Aucune compétence dans votre profil.'}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Use Profile OFF: Manual selection */}
                        {!useProfile && (
                            <div className="mt-4 pt-4 border-t border-green-200">
                                {/* Add occupation form */}
                                {isAddingOccupation && (
                                    <div className="flex items-start gap-2 mb-4">
                                        <div className="flex-1">
                                            <OccupationAutocomplete
                                                label=""
                                                language="fr"
                                                placeholder={t.offers?.form?.occupationPlaceholder || 'Recherchez un métier...'}
                                                onChange={handleAddOccupation}
                                            />
                                        </div>
                                        {selectedOccupations.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setIsAddingOccupation(false)}
                                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Selected occupations list */}
                                {selectedOccupations.length > 0 && (
                                    <div className="space-y-2">
                                        {selectedOccupations.map((occupation) => (
                                            <div key={occupation.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                                {/* Occupation header */}
                                                <div
                                                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                                                    onClick={() => handleToggleExpand(occupation.id)}
                                                >
                                                    <div className="flex-1">
                                                        <span className="font-medium text-gray-900">
                                                            {getDisplayName(occupation)}
                                                        </span>
                                                        {occupationSkills[occupation.id] && (
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {selectedSkillIds.filter(id =>
                                                                    occupationSkills[occupation.id].some(s => s.id === id)
                                                                ).length} / {occupationSkills[occupation.id].length} compétences
                                                            </p>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveOccupation(occupation.id);
                                                        }}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                    {expandedOccupation === occupation.id ? (
                                                        <ChevronUp className="w-5 h-5 text-gray-400" />
                                                    ) : (
                                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                                    )}
                                                </div>

                                                {/* Skills panel */}
                                                {expandedOccupation === occupation.id && (
                                                    <div className="px-3 pb-3 bg-gray-50">
                                                        {loadingSkills[occupation.id] ? (
                                                            <div className="flex items-center justify-center py-4">
                                                                <Loader2 className="w-5 h-5 text-green-600 animate-spin mr-2" />
                                                                <span className="text-sm text-gray-500">
                                                                    Chargement des compétences ESCO...
                                                                </span>
                                                            </div>
                                                        ) : occupationSkills[occupation.id]?.length > 0 ? (
                                                            <div className="space-y-3">
                                                                {/* Essential skills */}
                                                                {occupationSkills[occupation.id].filter(s => s.isEssential).length > 0 && (
                                                                    <div>
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <Sparkles className="w-4 h-4 text-amber-500" />
                                                                            <span className="text-sm font-medium text-gray-700">
                                                                                Compétences essentielles
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {occupationSkills[occupation.id]
                                                                                .filter(s => s.isEssential)
                                                                                .map(skill => (
                                                                                    <button
                                                                                        key={skill.id}
                                                                                        type="button"
                                                                                        onClick={() => handleSkillToggle(skill.id)}
                                                                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 border ${
                                                                                            selectedSkillIds.includes(skill.id)
                                                                                                ? 'bg-green-600 text-white border-green-600'
                                                                                                : 'bg-white text-gray-700 border-gray-200 hover:border-green-400'
                                                                                        }`}
                                                                                    >
                                                                                        {selectedSkillIds.includes(skill.id) && (
                                                                                            <Check className="w-3 h-3" />
                                                                                        )}
                                                                                        {skill.name_fr || skill.name_en}
                                                                                    </button>
                                                                                ))
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Optional skills */}
                                                                {occupationSkills[occupation.id].filter(s => !s.isEssential).length > 0 && (
                                                                    <div>
                                                                        <div className="flex items-center gap-2 mb-2 mt-3">
                                                                            <Check className="w-4 h-4 text-gray-400" />
                                                                            <span className="text-sm font-medium text-gray-700">
                                                                                Compétences optionnelles
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {occupationSkills[occupation.id]
                                                                                .filter(s => !s.isEssential)
                                                                                .map(skill => (
                                                                                    <button
                                                                                        key={skill.id}
                                                                                        type="button"
                                                                                        onClick={() => handleSkillToggle(skill.id)}
                                                                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 border ${
                                                                                            selectedSkillIds.includes(skill.id)
                                                                                                ? 'bg-green-600 text-white border-green-600'
                                                                                                : 'bg-white text-gray-700 border-gray-200 hover:border-green-400'
                                                                                        }`}
                                                                                    >
                                                                                        {selectedSkillIds.includes(skill.id) && (
                                                                                            <Check className="w-3 h-3" />
                                                                                        )}
                                                                                        {skill.name_fr || skill.name_en}
                                                                                    </button>
                                                                                ))
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2 py-4 text-gray-500">
                                                                <AlertCircle className="w-5 h-5" />
                                                                <span>Aucune compétence disponible pour ce métier</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* Add another occupation button */}
                                        {!isAddingOccupation && (
                                            <button
                                                type="button"
                                                onClick={() => setIsAddingOccupation(true)}
                                                className="flex items-center gap-2 w-full p-3 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg border border-dashed border-green-300 transition-colors"
                                            >
                                                <Briefcase className="w-4 h-4" />
                                                Ajouter un autre métier
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* No occupations yet message */}
                                {selectedOccupations.length === 0 && !isAddingOccupation && (
                                    <div className="text-center py-4">
                                        <p className="text-sm text-gray-500 mb-2">Aucun métier sélectionné</p>
                                        <button
                                            type="button"
                                            onClick={() => setIsAddingOccupation(true)}
                                            className="text-sm font-medium text-green-600 hover:text-green-700"
                                        >
                                            Ajouter un métier
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        {t.common?.cancel || 'Annuler'}
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        {t.offers?.form?.submit || "Publier l'offre"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateWorkOffer;
